export const MINIMUM_SAFE_TEXT_LETTERS = 3;

export const countLetters = (value) => {
    const matches = String(value ?? "").match(/[A-Za-z]/g);

    return matches ? matches.length : 0;
};

export const hasMinimumLetters = (
    value,
    minimumLetters = MINIMUM_SAFE_TEXT_LETTERS,
) => countLetters(value) >= minimumLetters;

export const getMinimumLettersMessage = (
    label,
    minimumLetters = MINIMUM_SAFE_TEXT_LETTERS,
) => `${label} must contain at least ${minimumLetters} letters.`;

const decodeHtmlCodePoint = (codePoint) => {
    const numericCodePoint = Number(codePoint);

    if (
        !Number.isInteger(numericCodePoint) ||
        numericCodePoint < 0 ||
        numericCodePoint > 0x10ffff
    ) {
        return "";
    }

    return String.fromCodePoint(numericCodePoint);
};

export const decodeBasicHtmlEntities = (value) =>
    String(value ?? "")
        .replace(/&nbsp;|&#160;|\u00a0/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&#(\d+);/g, (_, codePoint) =>
            decodeHtmlCodePoint(codePoint),
        )
        .replace(/&#x([0-9a-f]+);/gi, (_, codePoint) => {
            const numericCodePoint = Number.parseInt(codePoint, 16);

            return decodeHtmlCodePoint(numericCodePoint);
        });

export const getVisibleTextFromHtml = (value) =>
    decodeBasicHtmlEntities(
        String(value ?? "")
            .replace(/<!--[\s\S]*?-->/g, " ")
            .replace(/<(script|style|iframe)[^>]*>[\s\S]*?<\/\1>/gi, " ")
            .replace(/<br\s*\/?>/gi, " ")
            .replace(/<\/(p|div|li|tr|h[1-6])>/gi, " ")
            .replace(/<[^>]*>/g, " "),
    )
        .replace(/\s+/g, " ")
        .trim();

export const getPlainTextFromHtml = (value) =>
    decodeBasicHtmlEntities(
        String(value ?? "")
            .replace(/<!--[\s\S]*?-->/g, " ")
            .replace(/<(script|style|iframe)[^>]*>[\s\S]*?<\/\1>/gi, " ")
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/<\/(p|div|section|article|h[1-6])>/gi, "\n\n")
            .replace(/<\/li>/gi, "\n")
            .replace(/<li[^>]*>/gi, "- ")
            .replace(/<[^>]*>/g, ""),
    )
        .replace(/\r\n?/g, "\n")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n[ \t]+/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

const escapeHtmlText = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

export const plainTextToSimpleHtml = (value) => {
    const paragraphs = String(value ?? "")
        .replace(/\r\n?/g, "\n")
        .split(/\n\s*\n+/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);

    return paragraphs
        .map((paragraph) => {
            const htmlParagraph = paragraph
                .split("\n")
                .map((line) => escapeHtmlText(line.trim()))
                .join("<br />");

            return `<p>${htmlParagraph}</p>`;
        })
        .join("");
};

export const hasUnsafeHtml = (value) => {
    const html = String(value ?? "");

    return (
        /<\s*(script|iframe|object|embed|link|meta|form|input|button)\b/i.test(
            html,
        ) ||
        /<[^>]+\son[a-z]+\s*=/i.test(html) ||
        /javascript\s*:/i.test(html)
    );
};

export const hasMalformedTemplateToken = (value) => {
    const text = String(value ?? "");
    const openMatches = text.match(/{{/g) || [];
    const closeMatches = text.match(/}}/g) || [];

    return (
        openMatches.length !== closeMatches.length ||
        /{{\s*}}/.test(text) ||
        /{{[^}]*$/.test(text) ||
        /^[^{]*}}/.test(text)
    );
};

export const getTemplateTokenValidationMessage = (label, value) =>
    hasMalformedTemplateToken(value)
        ? `${label} has an incomplete template variable. Use matching {{ and }}.`
        : "";

export const getFallbackEmailBodyValidationMessage = (value) => {
    const rawBody = String(value ?? "");

    if (!rawBody.trim()) {
        return "Fallback email body is required.";
    }

    if (hasUnsafeHtml(rawBody)) {
        return "Fallback email body contains unsupported or unsafe HTML.";
    }

    const templateMessage = getTemplateTokenValidationMessage(
        "Fallback email body",
        rawBody,
    );

    if (templateMessage) return templateMessage;

    const visibleText = getVisibleTextFromHtml(rawBody);

    if (!visibleText) {
        return "Fallback email body must include readable email content.";
    }

    if (!hasMinimumLetters(visibleText)) {
        return getMinimumLettersMessage("Fallback email body");
    }

    return "";
};

export const getFallbackEmailValidationMessage = ({
    subject,
    html_body: htmlBody,
} = {}) => {
    const subjectText = String(subject ?? "").trim();

    if (!subjectText) {
        return "Every email campaign step needs a subject before launching.";
    }

    const subjectTemplateMessage = getTemplateTokenValidationMessage(
        "Fallback subject",
        subjectText,
    );

    if (subjectTemplateMessage) return subjectTemplateMessage;

    return getFallbackEmailBodyValidationMessage(htmlBody);
};
